import type { components } from '@/api/schema'

type ProjectAction = components['schemas']['ProjectOpenActionRead']

/** Where an action item is worked: the email that raised it, else its PO, order, customer thread or project. */
export type ActionTarget =
  | { kind: 'customer_thread'; threadId: number; messageId?: number; actionUuid?: string }
  | { kind: 'purchase_order'; poId: string; messageId?: number; actionUuid?: string }
  | { kind: 'order'; orderId: string; actionUuid: string }
  | { kind: 'project'; projectId: string; actionUuid: string }
  | { kind: 'invoice'; invoiceId: string; actionUuid: string }
  | { kind: 'tax_exemption'; formId: string; actionUuid: string }
  | { kind: 'warranty_item'; actionUuid: string }

export function actionTarget(action: ProjectAction, projectUuid: string): ActionTarget {
  // Accounting's invoice items (USA-1184) are worked on their invoice's page.
  if (action.action_source === 'ap_invoice' && action.invoice_uuid) {
    return { kind: 'invoice', invoiceId: action.invoice_uuid, actionUuid: action.uuid }
  }
  // A store customer's tax-exempt form (USA-1504) is decided on its form page.
  if (action.tax_exemption_uuid) {
    return { kind: 'tax_exemption', formId: action.tax_exemption_uuid, actionUuid: action.uuid }
  }
  // A warranty item (USA-915) is worked on its claim, which sits on the claim's purchase order; one no claim
  // holds yet (warranty@ mail nobody could tie to a claim) opens on its own.
  if (action.action_source === 'warranty_claim') {
    return action.po_uuid
      ? { kind: 'purchase_order', poId: action.po_uuid, actionUuid: action.uuid }
      : { kind: 'warranty_item', actionUuid: action.uuid }
  }
  // Raised by an email: open that email, where the PO page's card links to, so its context is one click away.
  const email = action.card?.source_email
  if (email?.thread_kind === 'customer' && email.thread_id != null) {
    return { kind: 'customer_thread', threadId: email.thread_id, messageId: email.message_id }
  }
  if (email && action.po_uuid && action.action_type !== 'po_review') {
    return { kind: 'purchase_order', poId: action.po_uuid, messageId: email.message_id }
  }
  // Raised by a customer email (USA-1328): worked on that email thread.
  if (action.action_source === 'customer_communication' && action.customer_thread_id != null) {
    return { kind: 'customer_thread', threadId: action.customer_thread_id, actionUuid: action.uuid }
  }
  // A manual-order line already on a PO is about that PO (USA-1468).
  if (action.action_source === 'order_line' && action.po_uuid) {
    return { kind: 'purchase_order', poId: action.po_uuid, actionUuid: action.uuid }
  }
  if (action.action_source === 'order_line' && action.order_uuid) {
    return { kind: 'order', orderId: action.order_uuid, actionUuid: action.uuid }
  }
  // Sales's "Review POs" (USA-1333) is worked in the project's PO rows, not on one PO.
  if (action.po_uuid && action.action_type !== 'po_review') {
    return { kind: 'purchase_order', poId: action.po_uuid, actionUuid: action.uuid }
  }
  return { kind: 'project', projectId: projectUuid, actionUuid: action.uuid }
}
